export const normalizeAssistantResponse = (response) => {
  if (!response) return {};

  const getValue = (primaryKey, fallbackKey) =>
    response[primaryKey] !== undefined
      ? response[primaryKey]
      : response[fallbackKey];

  const normalized = {
    Action: getValue("Action", "action") || "",
    BotMessage: getValue("BotMessage", "botMessage") || "",
    FlowSessionId: getValue("FlowSessionId", "flowSessionId") || "",
    CollectedData: getValue("CollectedData", "collectedData") || "",
    FlowCompleted: getValue("FlowCompleted", "flowCompleted") || false,
    RichContent: getValue("RichContent", "richContent") || "",
    StepMeta: getValue("StepMeta", "stepMeta") || null,
    ErrorCode: getValue("ErrorCode", "errorCode") || "",
    PopupTitle: getValue("PopupTitle", "popupTitle") || "",
    PopupDescription: getValue("PopupDescription", "popupDescription") || "",
    IsPopup: Boolean(getValue("isPopup", "isPopup")),
    FlagCode: getValue("flagCode", "FlagCode"),
    StatusCode: getValue("_status", "status"),
    PortalCall: null,
    QueuedMessage: getValue("QueuedMessage", "queuedMessage") || null,
    
  };

  [
    "BlobUrl",
    "blobUrl",
    "DownloadUrl",
    "downloadUrl",
    "ExcelBase64",
    "excelBase64",
    "ExcelFileName",
    "excelFileName",
  ].forEach((key) => {
    if (response[key] !== undefined) normalized[key] = response[key];
  });

  const portalCall = getValue("PortalCall", "portalCall");
  if (portalCall) {
    const getPortalValue = (primaryKey, fallbackKey) =>
      portalCall[primaryKey] !== undefined
        ? portalCall[primaryKey]
        : portalCall[fallbackKey];

    normalized.PortalCall = {
      Endpoint: getPortalValue("Endpoint", "endpoint") || "",
      Method: getPortalValue("Method", "method") || "POST",
      Template: getPortalValue("Template", "template") || "",
      ContentType: getPortalValue("ContentType", "contentType") || "json",
    };
  }

  if (normalized.StepMeta) {
    const stepMeta = normalized.StepMeta;
    const getStepValue = (primaryKey, fallbackKey) =>
      stepMeta[primaryKey] !== undefined
        ? stepMeta[primaryKey]
        : stepMeta[fallbackKey];

    normalized.StepMeta = {
      ProgressSteps: getStepValue("ProgressSteps", "progressSteps") || [],
      CurrentStepIdx: getStepValue("CurrentStepIdx", "currentStepIdx") ?? 0,
      CardType: getStepValue("CardType", "cardType") || "",
      CardData: getStepValue("CardData", "cardData") || null,
    };

    normalized.StepMeta.ProgressSteps = normalized.StepMeta.ProgressSteps.map(
      (step) => ({
        Label: step.Label || step.label || "",
        Status: step.Status || step.status || "pending",
      }),
    );
  }

  return normalized;
};

export const getApiErrorMessage = (response) =>
  response?.PopupDescription ||
  response?.PopupTitle ||
  response?.BotMessage ||
  response?.Message ||
  "Operation failed.";

export const getPortalValidationMessage = (endpoint, response) => {
  const normalizedEndpoint = String(endpoint || "").toLowerCase();
  const payload = response?.Payload || response;
  const globalCode = Number(payload?.GlobalCode);
  const message = payload?.Message || response?.Message || "";

  if (
    normalizedEndpoint.includes("/v1/cards/getcardinfobymobileno") &&
    globalCode === 1 &&
    message
  ) {
    return message;
  }

  return "";
};

export const hasApiError = (response) => {
  if (!response) return false;

  const candidates = [response];
  if (response && typeof response === "object" && response.Payload && typeof response.Payload === "object") {
    candidates.push(response.Payload);
  }
  if (response && typeof response === "object" && response.payload && typeof response.payload === "object") {
    candidates.push(response.payload);
  }

  for (const node of candidates) {
    if (!node || typeof node !== "object") continue;

    if (node.ErrorCode || node.errorCode) return true;

    const flagRaw = node.FlagCode !== undefined ? node.FlagCode : node.flagCode;
    if (flagRaw !== undefined && flagRaw !== null && flagRaw !== "") {
      const flag = Number(flagRaw);
      if (Number.isFinite(flag) && (flag < 0 || flag > 1)) return true;
    }

    const statusRaw = node.StatusCode !== undefined ? node.StatusCode : (node._status !== undefined ? node._status : node.status);
    if (statusRaw !== undefined && statusRaw !== null && statusRaw !== "") {
      const status = Number(statusRaw);
      if (Number.isFinite(status) && status !== 200 && status !== 0) return true;
    }

    const isPopup = node.isPopup === true || node.IsPopup === true;
    const popupTitle = node.PopupTitle || node.popupTitle || "";
    const popupDescription = node.PopupDescription || node.popupDescription || "";
    if (isPopup && (popupTitle || popupDescription)) return true;
  }

  return false;
};

export const extractApiErrorMessage = (response) => {
  if (!response) return "";

  const candidates = [];
  if (response && typeof response === "object") candidates.push(response);
  if (response && typeof response === "object" && response.Payload && typeof response.Payload === "object") {
    candidates.push(response.Payload);
  }
  if (response && typeof response === "object" && response.payload && typeof response.payload === "object") {
    candidates.push(response.payload);
  }

  for (const node of candidates) {
    if (!node || typeof node !== "object") continue;
    const msg =
      node.PopupDescription ||
      node.popupDescription ||
      node.PopupTitle ||
      node.popupTitle ||
      node.Message ||
      node.message ||
      node.BotMessage ||
      node.botMessage ||
      "";
    if (msg) return String(msg).replace(/\\r\\n/g, "\n").replace(/\r\n/g, "\n");
  }
  return "Operation failed.";
};

const SAFE_FILE_EXTENSIONS = [".xlsx", ".xls", ".csv", ".pdf"];
const SAFE_FILE_EXTENSION_LABELS = {
  ".xlsx": "XLSX",
  ".xls": "XLS",
  ".csv": "CSV",
  ".pdf": "PDF",
};
const SAFE_FILE_ACCEPT_ALIASES = {
  ".xlsx": [
    ".xlsx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/octet-stream",
  ],
  ".xls": [".xls", "application/vnd.ms-excel", "application/octet-stream"],
  ".csv": [
    ".csv",
    "text/csv",
    "application/csv",
    "application/vnd.ms-excel",
    "text/plain",
    "application/octet-stream",
  ],
  ".pdf": [
    ".pdf",
    "application/pdf",
    "application/x-pdf",
    "application/octet-stream",
  ],
};

const SAFE_FILE_MIME_TYPES = new Set(
  Object.values(SAFE_FILE_ACCEPT_ALIASES)
    .flat()
    .filter((token) => !token.startsWith(".")),
);

export const DEFAULT_SAFE_FILE_ACCEPT = SAFE_FILE_EXTENSIONS.join(",");

export const formatAllowedFileTypes = (extensions) => {
  const labels = extensions.map(
    (extension) =>
      SAFE_FILE_EXTENSION_LABELS[extension] || extension.toUpperCase(),
  );

  if (labels.length <= 1) return labels[0] || "supported";
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;

  return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
};

export const resolveSafeFileExtensions = (accept) => {
  const tokens = String(accept || "")
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  if (!tokens.length) return [...SAFE_FILE_EXTENSIONS];

  const matched = SAFE_FILE_EXTENSIONS.filter(
    (extension) =>
      tokens.includes(extension) ||
      SAFE_FILE_ACCEPT_ALIASES[extension].some((alias) =>
        tokens.includes(alias),
      ),
  );

  return matched.length ? matched : [...SAFE_FILE_EXTENSIONS];
};

export const getFileExtension = (fileName) => {
  const extensionIndex = String(fileName || "").lastIndexOf(".");
  return extensionIndex >= 0 ? fileName.slice(extensionIndex).toLowerCase() : "";
};

export const isSafeFileMimeType = (mimeType) => {
  const normalizedMimeType = String(mimeType || "").toLowerCase();
  return (
    !normalizedMimeType ||
    normalizedMimeType === "application/octet-stream" ||
    SAFE_FILE_MIME_TYPES.has(normalizedMimeType)
  );
};

export const formatApiMessageText = (message) =>
  String(message || "").replace(/\\r\\n/g, "\n").replace(/\r\n/g, "\n");
